import { KeyboardEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Employee, getEmployeeClientCompany } from "../../types/employees";

type EmployeeLookupSectionProps = {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSearchInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  onSelectEmployee: (employee: Employee) => void;
  searchResults: Employee[];
  highlightedResultIndex: number;
  onHighlightResult: (index: number) => void;
  isSearching: boolean;
  searchError: string | null;
  isLookupOpen: boolean;
};

const formatEmployeeName = (employee: Employee) => `${employee.fullName}`;

function EmployeeLookupSection({
  searchTerm,
  onSearchTermChange,
  onSearchInputKeyDown,
  onSearchFocus,
  onSearchBlur,
  onSelectEmployee,
  searchResults,
  highlightedResultIndex,
  onHighlightResult,
  isSearching,
  searchError,
  isLookupOpen,
}: EmployeeLookupSectionProps) {
  const { t } = useTranslation();
  const hasSearchTerm = searchTerm.trim().length > 0;
  const shouldShowResults =
    isLookupOpen &&
    hasSearchTerm &&
    (isSearching || searchResults.length > 0 || !!searchError);

  return (
    <section className="subCard" aria-label={t("Employee search")}>
      <p className="hintText sectionHint">
        {t("Search by name or national ID.")}
      </p>

      <label className="field" htmlFor="employee-search">
        <span className="fieldLabel">{t("Employee search")}</span>
        <input
          autoFocus
          autoComplete="off"
          className="inputControl"
          id="employee-search"
          onBlur={onSearchBlur}
          onChange={(event) => onSearchTermChange(event.target.value)}
          onFocus={onSearchFocus}
          onKeyDown={onSearchInputKeyDown}
          placeholder={t("Name or national ID")}
          role="combobox"
          aria-controls="employee-search-results"
          aria-expanded={shouldShowResults}
          aria-autocomplete="list"
          type="search"
          value={searchTerm}
        />
      </label>

      {shouldShowResults ? (
        <ul
          className="lookupResults"
          id="employee-search-results"
          role="listbox"
          aria-label={t("Employee search results")}
        >
          {isSearching ? (
            <li className="lookupState">{t("Searching employees...")}</li>
          ) : null}

          {!isSearching && searchResults.length === 0 && !searchError ? (
            <li className="lookupState">
              {t("No employees matched your search.")}
            </li>
          ) : null}

          {searchError ? (
            <li className="lookupState errorMessage" role="alert">
              {searchError}
            </li>
          ) : null}

          {!isSearching
            ? searchResults.map((employee, index) => (
                <li
                  key={employee.id}
                  role="option"
                  aria-selected={index === highlightedResultIndex}
                >
                  <button
                    className={`lookupResultButton ${
                      index === highlightedResultIndex ? "active" : ""
                    }`}
                    onClick={() => onSelectEmployee(employee)}
                    onMouseEnter={() => onHighlightResult(index)}
                    type="button"
                  >
                    <span className="lookupPrimaryText">
                      {formatEmployeeName(employee)}
                    </span>
                    <span className="lookupSecondaryText">
                      {getEmployeeClientCompany(employee)?.name ??
                        t("No client company")}
                      {employee.workstation?.name
                        ? ` / ${employee.workstation.name}`
                        : ""}
                      {employee.nationalId ? ` • ${employee.nationalId}` : ""}
                    </span>
                  </button>
                </li>
              ))
            : null}
        </ul>
      ) : null}
    </section>
  );
}

export default EmployeeLookupSection;
